import { useCallback, useEffect, useState } from 'react';
import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { PaginationUpdateEvent } from 'ui/paginationFooter';
import { paginate } from 'utils/arrayHelpers';
import { useSortingContext } from 'utils/contexts/sorting';
import { usePaginationContext } from 'utils/contexts/pagination';
import { useRolesContext } from '../contexts';
import { CombinedRole } from '../types';
import { sortRolesByKey } from '../utils';

export const useRolesTable = () => {
  const { sortBy, sortDirection, setSortBy, setSortDirection } = useSortingContext();
  const [sortedRoles, setSortedRoles] = useState<CombinedRole[]>([]);
  const [paginatedRoles, setPaginatedRoles] = useState<CombinedRole[]>([]);
  const { combinedRoles, isLoadingRoles } = useRolesContext();
  const { currentPage, pageSize, setCurrentPage, setPageSize } = usePaginationContext();

  const handlePaginationUpdate = useCallback(
    ({ pageSize: size, page }: PaginationUpdateEvent) => {
      setCurrentPage(page);
      setPageSize(size);
    },
    [setCurrentPage, setPageSize],
  );

  useEffect(() => {
    const isDescending = sortDirection === SORT_DIRECTIONS.DESCENDING;
    const sorted = sortRolesByKey(combinedRoles, sortBy, isDescending);
    setSortedRoles(sorted);
  }, [combinedRoles, sortBy, sortDirection]);

  useEffect(() => {
    const paginated = paginate(sortedRoles, pageSize, currentPage);
    setPaginatedRoles(paginated);
  }, [sortedRoles, pageSize, currentPage]);

  useEffect(() => {
    handlePaginationUpdate({ pageSize, page: 1 });
  }, [sortBy]);

  return {
    currentPage,
    handlePaginationUpdate,
    isLoadingRoles,
    pageSize,
    paginatedRoles,
    rolesLength: combinedRoles?.length,
    sortedRoles,
    setSortBy,
    setSortDirection,
    sortBy,
    sortDirection,
  };
};
